import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { AiPromptItem } from 'src/app/core/models/ai-prompt-item';
import { AiPromptTemplateLibraryService } from 'src/app/core/services/ai-prompt-template-library.service';
import { DialogService } from 'src/app/core/services/dialog.service';
import { DialogWidth } from 'src/app/shared/atlas-dialog/enums/dialog-width.enum';
import { LayoutService } from 'src/app/shared/atlas-layout/services/layout.service';
import { SearchAndSortValues } from 'src/app/shared/utils/list-utils';
import { AiPromptArgumentsDialogComponent } from './ai-prompt-arguments-dialog/ai-prompt-arguments-dialog.component';
import * as fromAiAssistantStore from 'src/app/core/store';
import { Store } from '@ngrx/store';

@Component({
    selector: 'atlas-ai-prompt-template-library-component',
    templateUrl: './ai-prompt-template-library.component.html',
    styleUrl: './ai-prompt-template-library.component.less',
    standalone: false
})
export class AiPromptTemplateLibraryComponent implements OnInit, OnDestroy {
  @Output() changeTab = new EventEmitter<number>();

  promptList$ = this.aiAssistantStore$.select(
    fromAiAssistantStore.getAiPromptTemplates
  );
  promptList: AiPromptItem[] = [];

  chipListItems = [
    'All',
    'Environment',
    'Family',
    'Finance',
    'Health',
    'Housing',
    'Lifestyle',
    'Marketing',
    'Motoring',
    'Social media'
  ];

  chipListControl: FormControl;
  dataSource: AiPromptItem[] = [];
  searchTemplateForm: UntypedFormGroup;
  isDesktopView: boolean;
  gridListColumns: number = 2;
  dynamicRowHeight: string;
  isFilterApplied: boolean;
  isChipSelected: boolean;
  private subscription: Subscription = new Subscription();

  constructor(
    public layoutService: LayoutService,
    private aiPromptTemplateLibraryService: AiPromptTemplateLibraryService,
    private fb: UntypedFormBuilder,
    private dialogService: DialogService,
    private aiAssistantStore$: Store<fromAiAssistantStore.State>
  ) {
    this.dataSource = this.promptList as AiPromptItem[];
  }

  ngOnInit(): void {
    const initialValue: SearchAndSortValues = {
      filter: ''
    } as SearchAndSortValues;
    this.searchTemplateForm = this.createForm(initialValue);

    this.subscription.add(
      this.layoutService.isDesktopView$.subscribe((isDesktopView) => {
        this.isDesktopView = isDesktopView;
        this.dynamicRowHeight = this.isDesktopView ? '8:4' : '1:0.80';
        this.gridListColumns = this.isDesktopView ? 2 : 1;
      })
    );
    // Initialize the chip list control
    this.subscription.add(
      this.chipListControl.valueChanges.subscribe((value) => {
        if (!value) {
          this.chipListControl.setValue('All');
          return;
        }
        value === 'All'
          ? (this.isChipSelected = false)
          : (this.isChipSelected = true);
        this.applyChipSelection(value);
      })
    );
    this.subscription.add(
      this.searchTemplateForm.controls['filter'].valueChanges.subscribe(
        (value) => {
          this.isFilterApplied = value != null && value !== '';
          this.applySearchFilter(value);
        }
      )
    );

    this.getAllPromtTemplates();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  createForm(initialLibraryValue: SearchAndSortValues) {
    this.chipListControl = new FormControl({
      value: 'All',
      disabled: false
    });
    return this.fb.group({
      filter: [initialLibraryValue.filter],
      selectedChipList: this.chipListControl
    });
  }

  selectPrompt(prompt: AiPromptItem): void {
    // Handle prompt selection logic here
    if (prompt.arguments && prompt.arguments.length > 0) {
      this.dialogService.show(
        AiPromptArgumentsDialogComponent,
        {
          width: DialogWidth.Small,
          data: {
            arguments: prompt.arguments
          },
          panelClass: 'dialog-full-width-height',
          disableClose: true
        },
        (data: Record<string, unknown>) => {
          this.switchToChatTab();
          // Handle the result from the dialog
          const args: Record<string, unknown> = data;
          this.getPromptTemplate(prompt, args);
        }
      );
    } else {
      this.switchToChatTab();
      const args: Record<string, unknown> = {};
      this.getPromptTemplate(prompt, args);
    }
  }

  getPromptTemplate(prompt: AiPromptItem, args: Record<string, unknown>): void {
    this.aiPromptTemplateLibraryService
      .getPromptTemplate(prompt.name, args)
      .subscribe((promptTemplate) => {
        this.aiPromptTemplateLibraryService.setCurrentPromptText(
          promptTemplate.promptText
        );
      });
  }

  switchToChatTab() {
    this.changeTab.emit(0);
  }

  applySearchFilter(filterValue: string) {
    const selectedChipValue = this.searchTemplateForm.controls[
      'selectedChipList'
    ].value as string;

    this.dataSource = this.applyPromptTemplateFilter(
      filterValue,
      selectedChipValue,
      this.promptList
    );
  }

  applyChipSelection(selectedChipValue: string): void {
    const filterValue = this.searchTemplateForm.controls['filter'].value;
    this.dataSource = this.applyPromptTemplateFilter(
      filterValue,
      selectedChipValue,
      this.promptList
    );
  }

  applyPromptTemplateFilter(
    filterValue: string,
    chipListValue: string,
    data: any[]
  ) {
    const hasFilterValue = filterValue != null && filterValue !== '';

    const searchFilteredItems = !hasFilterValue
      ? data
      : data.filter(
          (s) =>
            s.description
              .toLowerCase()
              .includes(filterValue.trim().toLowerCase()) ||
            s.title.toLowerCase().includes(filterValue.trim().toLowerCase())
        );

    if (!chipListValue || chipListValue === 'All') {
      return searchFilteredItems;
    }

    const chipListFilteredItems = searchFilteredItems.filter(
      (item) =>
        item.description
          .toLowerCase()
          .includes(chipListValue.trim().toLowerCase()) ||
        item.title.toLowerCase().includes(chipListValue.trim().toLowerCase())
    );
    return chipListFilteredItems;
  }

  private getAllPromtTemplates() {
    this.subscription.add(
      this.promptList$.subscribe((promptTemplates: AiPromptItem[]) => {
        this.promptList = promptTemplates;
        this.dataSource = this.promptList;
      })
    );
  }
}
